import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Radio,
  Plus,
  RefreshCw,
  Settings,
  Loader2,
  MessageSquare,
  AlertTriangle,
  Trash2,
  Edit2,
  Pause,
  Play,
  MoreVertical,
  X
} from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import listeningService, {
  Brand,
  Mention,
  ListeningStats as Stats,
  MentionFilters,
  CreateBrandData
} from '../../services/listeningService';
import Button from '../ui/Button';
import MentionCard from '../listening/MentionCard';
import MentionFiltersComponent from '../listening/MentionFilters';
import ListeningStats from '../listening/ListeningStats';
import BrandSetupModal from '../listening/BrandSetupModal';
import AiCostBadge from '../ai/AiCostBadge';

interface SocialListeningProps {
  loading?: boolean;
}

// Keywords antigas podem ter sido salvas como objeto ({ keyword, intent, ... }).
// Renderizar a string evita "[object Object]" no chip da marca.
const kwToString = (k: any): string =>
  typeof k === 'string' ? k : (k && (k.keyword || k.text || k.value || k.name)) || '';

const SocialListening: React.FC<SocialListeningProps> = ({ loading: parentLoading }) => {
  const { t } = useTranslation();
  const { currentProject } = useProject();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filters, setFilters] = useState<MentionFilters>({});
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentProject?.id) return;

    setIsLoading(true);
    try {
      const [brandsRes, mentionsRes, statsRes] = await Promise.all([
        listeningService.getBrands(currentProject.id),
        listeningService.getMentions(currentProject.id, { ...filters, limit: 20 }),
        listeningService.getStats(currentProject.id)
      ]);

      if (brandsRes.success) {
        setBrands(brandsRes.data);
      }

      if (mentionsRes.success) {
        setMentions(mentionsRes.data);
        setPagination({
          page: mentionsRes.pagination.page,
          totalPages: mentionsRes.pagination.totalPages,
          total: mentionsRes.pagination.total
        });
      }

      if (statsRes.success) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Error fetching listening data:', error);
      // Reset to empty state on error - no mock data
      setStats(null);
      setBrands([]);
      setMentions([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentProject?.id, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    if (!currentProject?.id || brands.length === 0) return;

    setIsRefreshing(true);
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      // Refresh mentions for all active brands
      const activeBrands = brands.filter((b) => b.is_monitoring);

      for (const brand of activeBrands) {
        const response = await listeningService.refreshBrandMentions(currentProject.id, brand.id, true);

        // If async mode, poll for result
        if (response.async && response.jobId) {
          await pollJobUntilComplete(response.jobId, undefined, 2000, 120000);
        }
      }

      toast.success(t('listening.mentionsUpdated', 'Mentions updated!'));
      await fetchData();
    } catch (error) {
      toast.error(t('listening.updateError', 'Error updating'));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCreateBrand = async (data: CreateBrandData) => {
    if (!currentProject?.id) return;

    try {
      await listeningService.createBrand(currentProject.id, data);
      toast.success(t('listening.brandConfigured', 'Brand configured!'));
      await fetchData();
    } catch (error) {
      toast.error(t('errors.generic', 'Error'));
      throw error;
    }
  };

  const handleUpdateBrand = async (brandId: string, data: CreateBrandData) => {
    if (!currentProject?.id) return;

    try {
      await listeningService.updateBrand(currentProject.id, brandId, data);
      toast.success(t('listening.brandUpdated', 'Brand updated!'));
      await fetchData();
    } catch (error) {
      toast.error(t('errors.generic', 'Error'));
      throw error;
    }
  };

  const handleDeleteBrand = async (brandId: string, brandName: string) => {
    if (!currentProject?.id) return;

    if (!confirm(t('listening.confirmDelete', 'Are you sure you want to delete this brand?').replace('{{name}}', brandName))) {
      return;
    }

    try {
      await listeningService.deleteBrand(currentProject.id, brandId);
      toast.success(t('listening.brandDeleted', 'Brand removed!'));
      await fetchData();
    } catch (error) {
      toast.error(t('errors.generic', 'Error'));
    }
  };

  const handleToggleMonitoring = async (brand: Brand) => {
    if (!currentProject?.id) return;

    try {
      await listeningService.updateBrand(currentProject.id, brand.id, {
        is_monitoring: !brand.is_monitoring
      });
      toast.success(brand.is_monitoring ? t('listening.monitoringPaused', 'Monitoring paused') : t('listening.monitoringEnabled', 'Monitoring enabled'));
      await fetchData();
    } catch (error) {
      toast.error(t('errors.generic', 'Error'));
    }
  };

  const handleSubmitBrand = async (data: CreateBrandData) => {
    if (editingBrand) {
      await handleUpdateBrand(editingBrand.id, data);
    } else {
      await handleCreateBrand(data);
    }
  };

  const handleMarkResponded = async (mentionId: string) => {
    if (!currentProject?.id) return;

    try {
      await listeningService.markAsResponded(currentProject.id, mentionId);
      setMentions((prev) =>
        prev.map((m) => (m.id === mentionId ? { ...m, responded: true } : m))
      );
      toast.success(t('listening.markedAsResponded', 'Marked as responded'));
    } catch (error) {
      toast.error(t('listening.errorMarking', 'Error marking'));
    }
  };

  const handleFilterChange = (newFilters: MentionFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleLoadMore = async () => {
    if (!currentProject?.id || pagination.page >= pagination.totalPages) return;

    try {
      const response = await listeningService.getMentions(currentProject.id, {
        ...filters,
        page: pagination.page + 1,
        limit: 20
      });

      if (response.success) {
        setMentions((prev) => [...prev, ...response.data]);
        setPagination({
          page: response.pagination.page,
          totalPages: response.pagination.totalPages,
          total: response.pagination.total
        });
      }
    } catch (error) {
      toast.error(t('listening.loadMoreError', 'Error loading more'));
    }
  };

  if (!currentProject) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {t('listening.selectProject', 'Select a project')}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {t('listening.selectProjectDesc', 'You need to select a project to access Social Listening')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('listening.title', 'Social Listening')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('listening.subtitle', 'Monitor brand mentions on social media')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              console.log('Filters clicked, current:', showFilters);
              setShowFilters(!showFilters);
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              showFilters
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Settings className="w-4 h-4" />
            {t('listening.filtersTitle', 'Filters')}
            {showFilters && <X className="w-3 h-3" />}
          </button>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isRefreshing || brands.length === 0}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t('listening.refresh', 'Refresh')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setEditingBrand(null);
              setShowBrandModal(true);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('listening.newBrand', 'New Brand')}
          </Button>
        </div>
      </div>
      {/* Custo de cada atualização das menções (o refresh roda IA para classificar) */}
      <AiCostBadge feature="ai-generation" action="listening-refresh" unitLabel={t('aiCredits.perRefresh', 'por atualização')} />

      {/* Stats */}
      <ListeningStats stats={stats} isLoading={isLoading} />

      {/* Brands List - Improved with actions */}
      {brands.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {t('listening.monitoredBrands', 'Monitored Brands')} ({brands.length})
            </h3>
          </div>
          <div className="space-y-2">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  brand.is_monitoring
                    ? brand.type === 'own'
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
                      : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${brand.is_monitoring ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                  <div>
                    <span className={`font-medium ${
                      brand.type === 'own'
                        ? 'text-primary-700 dark:text-primary-400'
                        : 'text-orange-700 dark:text-orange-400'
                    }`}>
                      {brand.name}
                    </span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      {brand.type === 'own' ? t('listening.myBrand', 'My brand') : t('listening.competitor', 'Competitor')}
                    </span>
                    {brand.keywords && brand.keywords.length > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {brand.keywords.slice(0, 3).map(kwToString).join(', ')}{brand.keywords.length > 3 ? ` +${brand.keywords.length - 3}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleMonitoring(brand)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      brand.is_monitoring
                        ? 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                        : 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30'
                    }`}
                    title={brand.is_monitoring ? t('listening.pauseMonitoring', 'Pause monitoring') : t('listening.enableMonitoring', 'Enable monitoring')}
                  >
                    {brand.is_monitoring ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingBrand(brand);
                      setShowBrandModal(true);
                    }}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title={t('listening.editBrand', 'Edit brand')}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteBrand(brand.id, brand.name)}
                    className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    title={t('listening.deleteBrand', 'Delete brand')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <MentionFiltersComponent
          filters={filters}
          brands={brands}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />
      )}

      {/* Mentions Feed */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-pulse"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
                    <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
                <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        ) : mentions.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {brands.length === 0
                ? t('listening.noBrands', 'No brands configured')
                : t('listening.noMentions', 'No mentions found')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {brands.length === 0
                ? t('listening.configureBrandToStart', 'Configure a brand to start monitoring')
                : t('listening.adjustFiltersOrWait', 'Try adjusting filters or wait for new mentions')}
            </p>
            {brands.length === 0 && (
              <Button
                variant="primary"
                onClick={() => setShowBrandModal(true)}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('listening.configureBrand', 'Configure Brand')}
              </Button>
            )}
          </div>
        ) : (
          <>
            {mentions.map((mention) => (
              <MentionCard
                key={mention.id}
                mention={mention}
                onMarkResponded={handleMarkResponded}
              />
            ))}

            {/* Load More */}
            {pagination.page < pagination.totalPages && (
              <div className="text-center pt-4">
                <Button variant="secondary" onClick={handleLoadMore}>
                  {t('listening.loadMore', 'Load more mentions')}
                </Button>
              </div>
            )}

            {/* Pagination Info */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {t('listening.showingOf', 'Showing {{shown}} of {{total}} mentions').replace('{{shown}}', mentions.length.toString()).replace('{{total}}', pagination.total.toString())}
            </p>
          </>
        )}
      </div>

      {/* Brand Setup Modal */}
      <BrandSetupModal
        isOpen={showBrandModal}
        onClose={() => {
          setShowBrandModal(false);
          setEditingBrand(null);
        }}
        onSubmit={handleSubmitBrand}
        onDelete={async (brandId) => {
          if (!currentProject?.id) return;
          await listeningService.deleteBrand(currentProject.id, brandId);
          toast.success(t('listening.brandDeleted', 'Brand removed!'));
          await fetchData();
        }}
        editingBrand={editingBrand}
      />
    </div>
  );
};

export default SocialListening;
